// src/services/businessStorageApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { 
  OMNISTACK_BASE_URL, 
  getPrepareHeaders 
} from '@/hooks/common/common-functions'

export const businessStorageApi = createApi({
  reducerPath: 'businessStorageApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${OMNISTACK_BASE_URL}business-storage`,
    prepareHeaders: (headers) => {
      getPrepareHeaders({
        headers,
        isClientApiKey: true,
      });
      
      const businessApiKey = localStorage.getItem('businessApiKey');
      if (businessApiKey) {
        headers.set('business-x-api-key', businessApiKey);
      }
      
      return headers;
    },
  }),
  tagTypes: ['BusinessFiles', 'StorageUsage'],
  endpoints: (builder) => ({
    // Upload file
    uploadFile: builder.mutation({
      query: ({ businessId, file, category = 'other' }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', category);
        
        return {
          url: `/${businessId}/upload`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['BusinessFiles', 'StorageUsage']
    }),
    
    // List files
    getFiles: builder.query({
      query: ({ businessId, category }) => ({
        url: `/${businessId}/files`,
        params: category ? { category } : {}
      }),
      providesTags: ['BusinessFiles']
    }),
    
    // Delete file
    deleteFile: builder.mutation({
      query: ({ businessId, fileName }) => ({
        url: `/${businessId}/files/${fileName}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['BusinessFiles', 'StorageUsage']
    }),
    
    // Bulk delete
    bulkDeleteFiles: builder.mutation({
      query: ({ businessId, fileNames }) => ({
        url: `/${businessId}/files`,
        method: 'DELETE',
        body: { fileNames }
      }),
      invalidatesTags: ['BusinessFiles', 'StorageUsage']
    }),
    
    // Get storage usage
    getStorageUsage: builder.query({
      query: (businessId) => ({
        url: `/${businessId}/storage/usage`
      }),
      providesTags: ['StorageUsage']
    }),
    
    // Get file info
    getFileInfo: builder.query({
      query: ({ businessId, fileName }) => ({
        url: `/${businessId}/files/${fileName}/info`
      }),
      providesTags: ['BusinessFiles']
    })
  })
});

export const {
  useUploadFileMutation,
  useGetFilesQuery,
  useDeleteFileMutation,
  useBulkDeleteFilesMutation,
  useGetStorageUsageQuery,
  useGetFileInfoQuery
} = businessStorageApi;